import React, { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useProcurement } from '@/context/ProcurementContext';
import { VendorInvoiceList, VendorInvoiceDetails } from '@/components/invoice';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  DollarSign, 
  CheckCircle, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Calendar
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const VendorInvoiceManagement = () => {
  const { state, actions } = useProcurement();
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (state.vendorInvoices.length === 0) {
      actions.fetchVendorInvoices();
    }
  }, []);

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
  };

  const handleBackToList = () => {
    setSelectedInvoice(null);
  };

  const handleVerifyInvoice = async (invoiceId: number) => {
    setLoading(true);
    try {
      const success = await actions.verifyVendorInvoice(invoiceId);
      if (success) {
        // Update the selected invoice if it's the one being verified
        if (selectedInvoice && selectedInvoice.id === invoiceId) {
          setSelectedInvoice({
            ...selectedInvoice,
            status: 'verified',
            verifiedDate: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error verifying invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId: number) => {
    setLoading(true);
    try {
      const success = await actions.payVendorInvoice(invoiceId);
      if (success) {
        // Update the selected invoice if it's the one being paid
        if (selectedInvoice && selectedInvoice.id === invoiceId) {
          setSelectedInvoice({
            ...selectedInvoice,
            status: 'paid',
            paidDate: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error paying invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate dashboard stats
  const totalInvoices = state.vendorInvoices.length;
  const pendingInvoices = state.vendorInvoices.filter(inv => inv.status === 'sent').length;
  const verifiedInvoices = state.vendorInvoices.filter(inv => inv.status === 'verified').length;
  const paidInvoices = state.vendorInvoices.filter(inv => inv.status === 'paid').length;
  const overdueInvoices = state.vendorInvoices.filter(inv => inv.status === 'disputed').length;
  
  const totalAmount = state.vendorInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
  const pendingAmount = state.vendorInvoices
    .filter(inv => inv.status === 'sent')
    .reduce((sum, inv) => sum + inv.totalAmount, 0);
  const paidAmount = state.vendorInvoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.totalAmount, 0);

  if (selectedInvoice) {
    return (
      <AppLayout>
        <VendorInvoiceDetails
          invoice={selectedInvoice}
          onBack={handleBackToList}
          onClose={handleBackToList}
          onVerify={handleVerifyInvoice}
          onPay={handlePayInvoice}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Vendor Invoice Management</h1>
            <p className="text-muted-foreground">Manage vendor invoices and payments</p>
          </div>
        </div>

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalInvoices}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(totalAmount)} total value
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Verification</CardTitle>
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingInvoices}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(pendingAmount)} pending
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Verified</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{verifiedInvoices}</div>
              <p className="text-xs text-muted-foreground">
                Ready for payment
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Paid</CardTitle>
              <DollarSign className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{paidInvoices}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(paidAmount)} paid
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Status Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Status Overview</CardTitle>
            <CardDescription>Current status distribution of vendor invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{pendingInvoices}</div>
                <div className="text-sm text-muted-foreground">Pending</div>
                <Badge variant="secondary" className="mt-1">Awaiting Verification</Badge>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{verifiedInvoices}</div>
                <div className="text-sm text-muted-foreground">Verified</div>
                <Badge variant="default" className="mt-1">Ready for Payment</Badge>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{paidInvoices}</div>
                <div className="text-sm text-muted-foreground">Paid</div>
                <Badge variant="default" className="mt-1">Completed</Badge>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{overdueInvoices}</div>
                <div className="text-sm text-muted-foreground">Overdue</div>
                <Badge variant="destructive" className="mt-1">Action Required</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice List */}
        <VendorInvoiceList onViewInvoice={handleViewInvoice} />
      </div>
    </AppLayout>
  );
};

export default VendorInvoiceManagement;

